import { relations } from "drizzle-orm/relations";
import {
  ccos,
  certifications,
  cities,
  coachDetails,
  coachExpertise,
  coachGroupPages,
  coachPoints,
  coaches,
  coachesCcos,
  coachingResults,
  expertiseCategories,
  expertiseSubcategories,
  invoices,
  offerBenefits,
  offerBonuses,
  offerComponents,
  offerFaqs,
  offerTags,
  offers,
  profiles,
  resources,
  testimonials,
  userPreferences,
  users,
  summits,
  summitDays,
  summitSpeakers,
  summitSessions,
  summitSessionTopics,
  summitRegistrations,
  summitSessionRegistrations,
} from "./schema";

export const coachesRelations = relations(coaches, ({ one, many }) => ({
  profile: one(profiles, {
    fields: [coaches.profileId],
    references: [profiles.id],
  }),
  details: one(coachDetails, {
    fields: [coaches.coachDetailsId],
    references: [coachDetails.id],
  }),
  mainOffer: one(offers, {
    fields: [coaches.mainOfferId],
    references: [offers.id],
  }),
  testimonials: many(testimonials),
  certifications: many(certifications),
  results: many(coachingResults),
  invoices: many(invoices),
  resources: many(resources),
  coachPoints: many(coachPoints),
  ccos: many(coachesCcos),
  expertises: many(coachExpertise),
}));

export const profilesRelations = relations(profiles, ({ one }) => ({
  coach: one(coaches, {
    fields: [profiles.coachId],
    references: [coaches.id],
  }),
}));

export const coachPointsRelations = relations(coachPoints, ({ one }) => ({
  coach: one(coaches, {
    fields: [coachPoints.coachId],
    references: [coaches.id],
  }),
}));

export const testimonialsRelations = relations(testimonials, ({ one }) => ({
  coach: one(coaches, {
    fields: [testimonials.coachId],
    references: [coaches.id],
  }),
}));

export const certificationsRelations = relations(certifications, ({ one }) => ({
  coach: one(coaches, {
    fields: [certifications.coachId],
    references: [coaches.id],
  }),
}));

export const resultsRelations = relations(coachingResults, ({ one }) => ({
  coach: one(coaches, {
    fields: [coachingResults.coachId],
    references: [coaches.id],
  }),
}));

export const offersRelations = relations(offers, ({ one, many }) => ({
  coach: one(coaches, {
    fields: [offers.coachId],
    references: [coaches.id],
  }),
  benefits: many(offerBenefits),
  components: many(offerComponents),
  tags: many(offerTags),
  bonuses: many(offerBonuses),
  faqs: many(offerFaqs),
}));

export const offerBenefitsRelations = relations(offerBenefits, ({ one }) => ({
  offer: one(offers, {
    fields: [offerBenefits.offerId],
    references: [offers.id],
  }),
}));

export const offerComponentsRelations = relations(
  offerComponents,
  ({ one }) => ({
    offer: one(offers, {
      fields: [offerComponents.offerId],
      references: [offers.id],
    }),
  }),
);

export const offerTagsRelations = relations(offerTags, ({ one }) => ({
  offer: one(offers, {
    fields: [offerTags.offerId],
    references: [offers.id],
  }),
}));

export const invoicesRelations = relations(invoices, ({ one }) => ({
  coach: one(coaches, {
    fields: [invoices.coachId],
    references: [coaches.id],
  }),
}));

export const resourcesRelations = relations(resources, ({ one }) => ({
  coach: one(coaches, {
    fields: [resources.coachId],
    references: [coaches.id],
  }),
}));

export const offerBonusesRelations = relations(offerBonuses, ({ one }) => ({
  offer: one(offers, {
    fields: [offerBonuses.offerId],
    references: [offers.id],
  }),
}));

export const offerFaqsRelations = relations(offerFaqs, ({ one }) => ({
  offer: one(offers, {
    fields: [offerFaqs.offerId],
    references: [offers.id],
  }),
}));

export const expertiseSubcategoriesRelations = relations(
  expertiseSubcategories,
  ({ one }) => ({
    category: one(expertiseCategories, {
      fields: [expertiseSubcategories.categoryId],
      references: [expertiseCategories.id],
    }),
  }),
);

export const coachExpertiseRelations = relations(coachExpertise, ({ one }) => ({
  subcategory: one(expertiseSubcategories, {
    fields: [coachExpertise.subcategoryId],
    references: [expertiseSubcategories.id],
  }),
  coach: one(coaches, {
    fields: [coachExpertise.coachId],
    references: [coaches.id],
  }),
}));

export const usersRelations = relations(users, ({ one }) => ({
  preferences: one(userPreferences, {
    fields: [users.id],
    references: [userPreferences.userId],
  }),
}));

export const userPreferencesRelations = relations(
  userPreferences,
  ({ one }) => ({
    user: one(users, {
      fields: [userPreferences.userId],
      references: [users.id],
    }),
  }),
);

export const ccosRelations = relations(ccos, ({ many }) => ({
  coachesCcos: many(coachesCcos),
}));

export const coachesCcosRelations = relations(coachesCcos, ({ one }) => ({
  coach: one(coaches, {
    fields: [coachesCcos.coachId],
    references: [coaches.id],
  }),
  cco: one(ccos, {
    fields: [coachesCcos.ccoId],
    references: [ccos.id],
  }),
}));

export const citiesRelations = relations(cities, () => ({}));

export const coachGroupPagesRelations = relations(
  coachGroupPages,
  ({ one }) => ({
    city: one(cities, {
      fields: [coachGroupPages.citySlug],
      references: [cities.slug],
    }),
    category: one(expertiseCategories, {
      fields: [coachGroupPages.categorySlug],
      references: [expertiseCategories.slug],
    }),
    subcategory: one(expertiseSubcategories, {
      fields: [coachGroupPages.subcategorySlug],
      references: [expertiseSubcategories.slug],
    }),
  }),
);

// Summit relations
export const summitsRelations = relations(summits, ({ many }) => ({
  days: many(summitDays),
  sessions: many(summitSessions),
  registrations: many(summitRegistrations),
}));

export const summitDaysRelations = relations(summitDays, ({ one, many }) => ({
  summit: one(summits, {
    fields: [summitDays.summitId],
    references: [summits.id],
  }),
  sessions: many(summitSessions),
}));

export const summitSpeakersRelations = relations(
  summitSpeakers,
  ({ many }) => ({
    sessions: many(summitSessions),
  }),
);

export const summitSessionsRelations = relations(
  summitSessions,
  ({ one, many }) => ({
    summit: one(summits, {
      fields: [summitSessions.summitId],
      references: [summits.id],
    }),
    speaker: one(summitSpeakers, {
      fields: [summitSessions.speakerId],
      references: [summitSpeakers.id],
    }),
    day: one(summitDays, {
      fields: [summitSessions.summitDayId],
      references: [summitDays.id],
    }),
    topics: many(summitSessionTopics),
    registrations: many(summitSessionRegistrations),
  }),
);

export const summitSessionTopicsRelations = relations(
  summitSessionTopics,
  ({ one }) => ({
    session: one(summitSessions, {
      fields: [summitSessionTopics.sessionId],
      references: [summitSessions.id],
    }),
  }),
);

export const summitRegistrationsRelations = relations(
  summitRegistrations,
  ({ one }) => ({
    summit: one(summits, {
      fields: [summitRegistrations.summitId],
      references: [summits.id],
    }),
    user: one(users, {
      fields: [summitRegistrations.userId],
      references: [users.id],
    }),
  }),
);

export const summitSessionRegistrationsRelations = relations(
  summitSessionRegistrations,
  ({ one }) => ({
    session: one(summitSessions, {
      fields: [summitSessionRegistrations.sessionId],
      references: [summitSessions.id],
    }),
    user: one(users, {
      fields: [summitSessionRegistrations.userId],
      references: [users.id],
    }),
  }),
);
